import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { LoadingSpinner } from './components/ui/LoadingSpinner';

// Features
import { AuthCallback } from './features/auth/AuthCallback';
import { ProtectedRoute } from './features/auth/ProtectedRoute';

// Lazy loaded components
const LandingPage = React.lazy(() => import('./features/landing/LandingPage').then(module => ({ default: module.LandingPage })));
const DashboardLayout = React.lazy(() => import('./components/layout/DashboardLayout').then(module => ({ default: module.DashboardLayout })));
const Overview = React.lazy(() => import('./features/dashboard/Overview').then(module => ({ default: module.Overview })));
const LeaderboardPage = React.lazy(() => import('./features/leaderboard/LeaderboardPage').then(module => ({ default: module.LeaderboardPage })));
const TeamsPage = React.lazy(() => import('./features/teams/TeamsPage').then(module => ({ default: module.TeamsPage })));
const TeamDetailPage = React.lazy(() => import('./features/teams/TeamDetailPage').then(module => ({ default: module.TeamDetailPage })));
const ComparisonPage = React.lazy(() => import('./features/comparison/ComparisonPage').then(module => ({ default: module.ComparisonPage })));
const RaceDetailPage = React.lazy(() => import('./features/results/RaceDetailPage').then(module => ({ default: module.RaceDetailPage })));

const PageLoader = () => (
  <div className="h-full w-full flex items-center justify-center min-h-[50vh]">
    <LoadingSpinner size="lg" label="Loading content..." />
  </div>
);

export default function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
     return (
       <div className="min-h-screen bg-background flex items-center justify-center">
         <LoadingSpinner size="lg" label="Initializing..." />
       </div>
     );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
           isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Suspense fallback={<PageLoader />}>
              <LandingPage />
            </Suspense>
          )
        }
      />
      
      <Route path="/oauth/callback" element={<AuthCallback />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <DashboardLayout />
            </Suspense>
          </ProtectedRoute>
        }
      >
        <Route index element={
          <Suspense fallback={<PageLoader />}>
            <Overview />
          </Suspense>
        } />
        <Route path="leaderboard" element={
          <Suspense fallback={<PageLoader />}>
            <LeaderboardPage />
          </Suspense>
        } />
        <Route path="teams" element={
          <Suspense fallback={<PageLoader />}>
            <TeamsPage />
          </Suspense>
        } />
        <Route path="teams/:teamId" element={
          <Suspense fallback={<PageLoader />}>
            <TeamDetailPage />
          </Suspense>
        } />
        <Route path="compare" element={
          <Suspense fallback={<PageLoader />}>
            <ComparisonPage />
          </Suspense>
        } />
        <Route path="race/:subsessionId" element={
          <Suspense fallback={<PageLoader />}>
            <RaceDetailPage />
          </Suspense>
        } />
        <Route path="driver/:id" element={
          <Suspense fallback={<PageLoader />}>
            <Overview />
          </Suspense>
        } />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
